/*
 * Author: Roger
 * File Created: Wednesday, 24-09-25 12:38 am
 * Last Modified: Thursday, 25-03-06 11:11 am, by Ben Zhao
 */

import { useCallback, useEffect } from 'react';

import { produce } from 'immer';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import { dateFormat } from '@web/utils';

import type {
  ReferralConfig,
  RewardType,
  UserBoostProgress,
  UserMonthRebate,
  UserRebateDashboard,
  UserRebateRate,
  UserRebateSummary,
} from '@/@types/marketing';
import {
  getUserBoostProgress,
  getUserDailyRebates,
  getUserMonthRebates,
  getUserRebateDashboard,
  getUserRebateRate,
  getUserRebateSummary,
  getUserReferralConfig,
} from '@/services/marketing.service';
import logger from '@/utils/logger';
import { formatToPercent } from '@/utils/number';

import { useUserInfo } from './useUserStore';

export interface MonthDataItem {
  year: string;
  data: UserMonthRebate[];
}

interface RebateStoreState {
  rebateRate?: UserRebateRate;
  rebateDashboard?: UserRebateDashboard;
  rebateGradings?: string[][];
  boostProgress?: UserBoostProgress;
  boostGradings?: string[][];
  dailyRebates: Record<RewardType, Record<string, string> | undefined>;
  dailyTotal: Record<RewardType, string>;
  monthRebates: Record<RewardType, MonthDataItem[] | undefined>;
  rebateSummary?: UserRebateSummary;
  referralConfig?: ReferralConfig;
}

interface RebateStoreAction {
  clearData: () => void;
  getRebateRate: () => Promise<UserRebateRate>;
  getRebateDashboard: () => Promise<{
    dashboard: UserRebateDashboard;
    gradings: string[][];
  }>;
  getBoostProgress: () => Promise<{
    progress: UserBoostProgress;
    gradings: string[][];
  }>;
  getDailyRebates: (
    _type: RewardType,
  ) => Promise<{ daily: Record<string, string>; total: string }>;
  getMonthRebates: (_type: RewardType) => Promise<MonthDataItem[]>;
  getRebateSummary: () => Promise<UserRebateSummary>;
  getReferralConfig: () => Promise<ReferralConfig>;
}

const defaultState: RebateStoreState = {
  rebateRate: undefined,
  rebateDashboard: undefined,
  rebateGradings: [],
  boostProgress: undefined,
  boostGradings: [],
  dailyRebates: { REBATE: undefined, REFERRAL: undefined },
  dailyTotal: { REBATE: '0', REFERRAL: '0' },
  monthRebates: { REBATE: undefined, REFERRAL: undefined },
  rebateSummary: undefined,
  referralConfig: undefined,
};

export const useRebateStore = create<RebateStoreState & RebateStoreAction>(
  (set, get) => ({
    clearData: () => {
      set(() => defaultState);
    },

    rebateRate: undefined,
    getRebateRate: async () => {
      const { rebateRate } = get();

      if (rebateRate) {
        return rebateRate;
      }

      const { data } = await getUserRebateRate();

      set(
        produce((state: RebateStoreState) => {
          state.rebateRate = data;
        }),
      );

      return data;
    },

    rebateDashboard: undefined,
    getRebateDashboard: async () => {
      const { rebateDashboard, rebateGradings } = get();

      if (rebateDashboard && rebateGradings) {
        return { dashboard: rebateDashboard, gradings: rebateGradings };
      }

      const { data } = await getUserRebateDashboard();
      const gradings = data.gradings.map((item) => [
        `$${item.lowerLimit}<=N${item.upperLimit < '99999999' ? `<$${item.upperLimit}` : ''}`,
        formatToPercent(Number(item.rate)),
        `${item.cardLevel}`,
      ]);

      set(
        produce((state: RebateStoreState) => {
          state.rebateDashboard = data;
          state.rebateGradings = gradings;
        }),
      );

      return { dashboard: data, gradings };
    },

    boostProgress: undefined,
    getBoostProgress: async () => {
      const { boostProgress, boostGradings } = get();

      if (boostProgress && boostGradings) {
        return { progress: boostProgress, gradings: boostGradings };
      }

      const { data } = await getUserBoostProgress();
      const gradings = data.gradings.map((item) => [
        `$${item.lowerLimit}<=MS${item.upperLimit < '99999999' ? `<$${item.upperLimit}` : ''}`,
        `MS * ${formatToPercent(Number(item.rate))}`,
      ]);

      set(
        produce((state: RebateStoreState) => {
          state.boostProgress = data;
          state.boostGradings = gradings;
        }),
      );

      return { progress: data, gradings };
    },

    dailyRebates: { REBATE: undefined, REFERRAL: undefined },
    dailyTotal: { REBATE: '0', REFERRAL: '0' },
    getDailyRebates: async (type: RewardType) => {
      const { dailyRebates, dailyTotal } = get();

      if (dailyRebates[type] && dailyTotal[type]) {
        return { daily: dailyRebates[type], total: dailyTotal[type] };
      }

      const { data } = await getUserDailyRebates({ rewardType: type });
      let total = 0;
      const _dailyRebates: Record<string, string> = {};

      data.forEach((item) => {
        _dailyRebates[
          dateFormat(new Date(Number(item.rewardDate)), 'YYYY-MM-DD', true)
        ] = item.totalRewardAmount;
        total = total + Number(item.totalRewardAmount);
      });

      set(
        produce((state: RebateStoreState) => {
          state.dailyRebates[type] = _dailyRebates;
          state.dailyTotal[type] = total.toString();
        }),
      );

      return { daily: _dailyRebates, total: total.toString() };
    },

    monthRebates: { REBATE: undefined, REFERRAL: undefined },
    getMonthRebates: async (type: RewardType) => {
      const { monthRebates } = get();

      if (monthRebates[type]) {
        return monthRebates[type];
      }

      let data: UserMonthRebate[] = [];

      try {
        const res = await getUserMonthRebates({ rewardType: type });

        data = res.data;
      } catch (error) {
        data = [];
      }

      const _monthData: Record<string, UserMonthRebate[]> = {};

      data
        .sort((a, b) => b.distributionMonth.localeCompare(a.distributionMonth))
        .forEach((item: UserMonthRebate) => {
          if (!/^\d{4}-\d{2}$/.test(item.distributionMonth)) return;

          const [year, month] = item.distributionMonth.split('-');

          if (typeof year !== 'string' || typeof month !== 'string') return;

          if (!_monthData[year]) {
            _monthData[year] = [];
          }

          _monthData[year].push(item);
        });

      const _monthRebates: MonthDataItem[] = Object.keys(_monthData)
        .map((year) => {
          return { year, data: _monthData[year]! };
        })
        .sort((a, b) => Number(b.year) - Number(a.year));

      set(
        produce((state: RebateStoreState) => {
          state.monthRebates[type] = _monthRebates;
        }),
      );

      return _monthRebates;
    },

    rebateSummary: undefined,
    getRebateSummary: async () => {
      const { rebateSummary } = get();

      if (rebateSummary) {
        return rebateSummary;
      }

      const { data } = await getUserRebateSummary();

      set(
        produce((state: RebateStoreState) => {
          state.rebateSummary = data;
        }),
      );

      return data;
    },

    referralConfig: undefined,
    getReferralConfig: async () => {
      const { referralConfig } = get();

      if (referralConfig) {
        return referralConfig;
      }

      const { data } = await getUserReferralConfig();

      set(
        produce((state: RebateStoreState) => {
          state.referralConfig = data;
        }),
      );

      return data;
    },
  }),
);

export const useUserRebateRate = () => {
  const { rebateRate, getRebateRate } = useRebateStore(
    useShallow((state) => ({
      rebateRate: state.rebateRate,
      getRebateRate: state.getRebateRate,
    })),
  );

  const { isLogin } = useUserInfo();

  const fetchRebateRate = useCallback(async () => {
    try {
      await getRebateRate();
    } catch (error) {
      logger.error('Fail to get UserRebateRate: ', error);
    }
  }, [getRebateRate]);

  useEffect(() => {
    if (isLogin && !rebateRate) {
      void fetchRebateRate();
    }
  }, [isLogin, fetchRebateRate, rebateRate]);

  return rebateRate;
};

export const useUserRebateData = () => {
  const {
    rebateDashboard,
    rebateGradings,
    boostProgress,
    boostGradings,
    getRebateDashboard,
    getBoostProgress,
  } = useRebateStore(
    useShallow((state) => ({
      rebateDashboard: state.rebateDashboard,
      rebateGradings: state.rebateGradings,
      boostProgress: state.boostProgress,
      boostGradings: state.boostGradings,
      getRebateDashboard: state.getRebateDashboard,
      getBoostProgress: state.getBoostProgress,
    })),
  );

  const { isLogin } = useUserInfo();

  const fetchRebateDashboard = useCallback(async () => {
    try {
      await getRebateDashboard();
    } catch (error) {
      logger.error('Fail to get UserRebateDashboard: ', error);
    }
  }, [getRebateDashboard]);

  const fetchBoostProgress = useCallback(async () => {
    try {
      await getBoostProgress();
    } catch (error) {
      logger.error('Fail to get UserBoostProgress: ', error);
    }
  }, [getBoostProgress]);

  useEffect(() => {
    if (isLogin) {
      if (!rebateDashboard) {
        void fetchRebateDashboard();
      }

      if (!boostProgress) {
        void fetchBoostProgress();
      }
    }
  }, [
    isLogin,
    rebateDashboard,
    rebateGradings,
    fetchRebateDashboard,
    boostProgress,
    boostGradings,
    fetchBoostProgress,
  ]);

  return { rebateDashboard, rebateGradings, boostProgress, boostGradings };
};

export const useDailyRebateData = (type: RewardType = 'REBATE') => {
  const { dailyRebates, dailyTotal, getDailyRebates } = useRebateStore(
    useShallow((state) => ({
      dailyRebates: state.dailyRebates,
      dailyTotal: state.dailyTotal,
      getDailyRebates: state.getDailyRebates,
    })),
  );

  const { isLogin } = useUserInfo();

  const fetchDailyRebates = useCallback(async () => {
    try {
      await getDailyRebates(type);
    } catch (error) {
      logger.error('Fail to get DailyRebates: ', error);
    }
  }, [getDailyRebates, type]);

  useEffect(() => {
    if (isLogin && !dailyRebates[type]) {
      void fetchDailyRebates();
    }
  }, [isLogin, dailyRebates, dailyTotal, fetchDailyRebates, type]);

  return { dailyRebates: dailyRebates[type], dailyTotal: dailyTotal[type] };
};

export const useMonthRebateData = (type: RewardType = 'REBATE') => {
  const { monthRebates, getMonthRebates } = useRebateStore(
    useShallow((state) => ({
      monthRebates: state.monthRebates,
      getMonthRebates: state.getMonthRebates,
    })),
  );

  const { isLogin } = useUserInfo();

  const fetchMonthRebates = useCallback(async () => {
    try {
      await getMonthRebates(type);
    } catch (error) {
      logger.error('Fail to get MonthRebates: ', error);
    }
  }, [getMonthRebates, type]);

  useEffect(() => {
    if (isLogin && !monthRebates[type]) {
      void fetchMonthRebates();
    }
  }, [isLogin, monthRebates, fetchMonthRebates, type]);

  return { monthRebates: monthRebates[type] };
};

export const useRebateSummary = () => {
  const { rebateSummary, getRebateSummary } = useRebateStore(
    useShallow((state) => ({
      rebateSummary: state.rebateSummary,
      getRebateSummary: state.getRebateSummary,
    })),
  );

  const { isLogin } = useUserInfo();

  const fetchRebateSummary = useCallback(async () => {
    try {
      await getRebateSummary();
    } catch (error) {
      logger.error('Fail to get RebateSummary: ', error);
    }
  }, [getRebateSummary]);

  useEffect(() => {
    if (isLogin && !rebateSummary) {
      void fetchRebateSummary();
    }
  }, [isLogin, rebateSummary, fetchRebateSummary]);

  return { rebateSummary };
};

export const useReferralConfig = () => {
  const { referralConfig, getReferralConfig } = useRebateStore(
    useShallow((state) => ({
      referralConfig: state.referralConfig,
      getReferralConfig: state.getReferralConfig,
    })),
  );

  const { isLogin } = useUserInfo();

  const fetchReferralConfig = useCallback(async () => {
    try {
      await getReferralConfig();
    } catch (error) {
      logger.error('Fail to get ReferralConfig: ', error);
    }
  }, [getReferralConfig]);

  useEffect(() => {
    if (isLogin && referralConfig === undefined) {
      void fetchReferralConfig();
    }
  }, [isLogin, referralConfig, fetchReferralConfig]);

  return referralConfig;
};
